export type BoardPositionItem = {
  id: string
  board_position: number | null
}

/** Insert index within a column; midpoint of neighbors (float positions). */
export function computeBoardPosition(
  columnItems: BoardPositionItem[],
  insertIndex: number,
): number {
  if (columnItems.length === 0) return 1000

  if (insertIndex <= 0) {
    const first = columnItems[0].board_position
    return (first ?? 1000) - 1000
  }

  if (insertIndex >= columnItems.length) {
    const last = columnItems[columnItems.length - 1].board_position
    return (last ?? 0) + 1000
  }

  const before = columnItems[insertIndex - 1].board_position ?? 0
  const after = columnItems[insertIndex].board_position ?? before + 2000
  return (before + after) / 2
}

export function sortApplicantsByBoardPosition<T extends BoardPositionItem & { applied_at: string }>(
  applicants: T[],
): T[] {
  return [...applicants].sort((a, b) => {
    const ap = a.board_position ?? Number.POSITIVE_INFINITY
    const bp = b.board_position ?? Number.POSITIVE_INFINITY
    if (ap !== bp) return ap - bp
    return new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime()
  })
}

export type BoardOrderEntry = {
  application_id: string
  board_position: number
}

/** One-shot per-column sort: sequential positions within each column. */
export function buildColumnSortOrderings(
  columns: Array<{ items: BoardPositionItem[] }>,
  sortKey: 'score' | 'newest',
  scoreFor: (id: string) => number | null,
  appliedAtFor: (id: string) => string,
): BoardOrderEntry[] {
  const orderings: BoardOrderEntry[] = []

  for (const column of columns) {
    const sorted = [...column.items].sort((a, b) => {
      if (sortKey === 'score') {
        const as = scoreFor(a.id)
        const bs = scoreFor(b.id)
        if (as == null && bs == null) return 0
        if (as == null) return 1
        if (bs == null) return -1
        return bs - as
      }
      return new Date(appliedAtFor(b.id)).getTime() - new Date(appliedAtFor(a.id)).getTime()
    })

    sorted.forEach((item, index) => {
      orderings.push({
        application_id: item.id,
        board_position: index * 1000,
      })
    })
  }

  return orderings
}
